import { createServerFn } from "@tanstack/react-start";
import { redirect } from "@tanstack/react-router";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  createAdminSchool,
  createSchooldocentInvite,
  deleteSchool,
  deleteSchooldocentInvite,
  generateSchoolAlias,
  getAdminScholen,
  getAdminSchoolDetail,
  getDocentenForSchool,
  getSchooldocentInvitesForSchoolAdmin,
  removeDocentFromSchool,
  resendSchooldocentInvite,
  setHoofdDocentForSchool,
  updateLicentieAantalLeerlingen,
  updateLicentieEndDate,
  updateLicentieType,
  updateLicentieVerwerkersovereenkomst,
  updateMaxDocenten,
  updateSchoolAlias,
  updateSchoolBestuur,
  updateSchoolBrin,
  updateSchoolContactgegevens,
  updateSchoolExcludeFromStatistics,
  updateSchoolIsZuurpruim,
  updateSchoolNaam,
} from "#/modules/admin/api.schools-admin.server";
import { contactgegevensAdminSchema } from "#/modules/admin/schema.contactgegevens-admin";
import { createSchoolAdminSchema } from "#/modules/admin/schema.create-school-admin";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import type { SchoolsAdminSearch } from "#/modules/admin/schema.search";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { m } from "#/paraglide/messages";

/** GET /admin/scholen — school overview list (searchable, sortable, paginated). */
export const getAdminScholenOverview = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator((data: SchoolsAdminSearch) => data)
  .handler(async ({ data }) => {
    const result = await getAdminScholen(data);
    if (Result.isError(result)) throw result.error;

    const totalCount = result.value?.count ?? 0;
    return {
      scholen: result.value?.data ?? [],
      totalPages: Math.ceil(totalCount / data.pageSize),
      totalCount,
    };
  });

/** GET — layout data for the `$id` school detail: docenten, school, invites. */
export const getAdminSchoolLayoutData = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator((data: { schoolId: string }) => z.object({ schoolId: z.string() }).parse(data))
  .handler(async ({ data }) => {
    const [docentenResult, schoolResult, invitesResult] = await Promise.all([
      getDocentenForSchool(data.schoolId),
      getAdminSchoolDetail(data.schoolId),
      getSchooldocentInvitesForSchoolAdmin(data.schoolId),
    ]);
    if (Result.isError(docentenResult)) throw docentenResult.error;
    if (Result.isError(schoolResult)) throw schoolResult.error;
    if (Result.isError(invitesResult)) throw invitesResult.error;
    return {
      docenten: docentenResult.value?.docenten ?? [],
      docentenCount: docentenResult.value?.docenten?.length ?? 0,
      school: schoolResult.value?.school,
      kpis: schoolResult.value?.kpis,
      invites: invitesResult.value?.invites ?? [],
      canAddDocent: invitesResult.value?.canAddDocent ?? false,
    };
  });

/** POST /admin/scholen — create a school. Redirects to the overview on success. */
export const createAdminSchoolFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator((data: { locale: string; schoolyear?: string; input: unknown }) => ({
    locale: z.string().min(1).parse(data.locale),
    schoolyear: data.schoolyear,
    input: createSchoolAdminSchema.parse(data.input),
  }))
  .handler(async ({ data }) => {
    const value = data.input;
    const result = await createAdminSchool({
      name: value.name,
      alias: value.alias || undefined,
      brin: value.brin || undefined,
      address: value.address,
      houseNumber: value.houseNumber,
      postalCode: value.postalCode,
      city: value.city,
      phoneNumber: value.phoneNumber,
      bestuursnummer: value.bestuursnummer || undefined,
      bestuursnaam: value.bestuursnaam || undefined,
      licentieType: value.licentieType,
      endDate: value.endDate ? `${value.endDate}T00:00:00.000Z` : undefined,
    });

    if (Result.isError(result)) {
      // Backend 422 codes ("MinLength"/"MaxLength") are not user-facing — replace per field.
      const fieldErrors = toFieldErrors(result.error, {
        brin: () => m["brin.form_errors.invalid_brin"](),
      });
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }

    throw redirect({
      to: "/$locale/{-$schoolyear}/admin/schools",
      params: { locale: data.locale, schoolyear: data.schoolyear },
    });
  });

/** GET — alias suggestion for a school name; empty name yields an empty alias. */
export const generateSchoolAliasFn = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator((data: { name: string }) => z.object({ name: z.string().max(150) }).parse(data))
  .handler(async ({ data }) => {
    const name = data.name.trim();
    if (name.length === 0) return { alias: "" };

    const result = await generateSchoolAlias(name);
    if (Result.isError(result)) return { alias: "" };
    return { alias: result.value?.alias ?? "" };
  });

/** POST — delete a school. Redirects to the overview on success. */
export const deleteSchoolFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; locale: string; schoolyear?: string }) =>
    z
      .object({
        schoolId: z.string().min(1),
        locale: z.string().min(1),
        schoolyear: z.string().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const result = await deleteSchool({ schoolId: data.schoolId });
    if (Result.isError(result)) {
      throw new Error(m["common.something_went_wrong"]());
    }
    throw redirect({
      to: "/$locale/{-$schoolyear}/admin/schools",
      params: { locale: data.locale, schoolyear: data.schoolyear },
    });
  });

/** POST — update the max number of docenten. */
export const updateMaxDocentenFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; maxDocenten: number }) =>
    z.object({ schoolId: z.string().min(1), maxDocenten: z.number().int().min(0) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateMaxDocenten({
      schoolId: data.schoolId,
      maxDocenten: data.maxDocenten,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — update the school alias (empty string clears it). */
export const updateSchoolAliasFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; alias: string | null }) =>
    z.object({ schoolId: z.string().min(1), alias: z.string().max(150).nullable() }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateSchoolAlias({
      schoolId: data.schoolId,
      alias: data.alias,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — rename the school. The backend re-slugs the tenant, so its portal URLs change. */
export const updateSchoolNaamFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; name: string }) =>
    z.object({ schoolId: z.string().min(1), name: z.string().min(1).max(150) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateSchoolNaam({ schoolId: data.schoolId, name: data.name });
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST — update the BRIN. Rejected when another school already holds it. */
export const updateSchoolBrinFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; brin: string }) =>
    z.object({ schoolId: z.string().min(1), brin: z.string().length(6) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateSchoolBrin({ schoolId: data.schoolId, brin: data.brin });
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST — update the school bestuur (empty strings clear it). */
export const updateSchoolBestuurFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(
    (data: { schoolId: string; bestuursnummer: string | null; bestuursnaam: string | null }) =>
      z
        .object({
          schoolId: z.string().min(1),
          bestuursnummer: z.string().max(150).nullable(),
          bestuursnaam: z.string().max(150).nullable(),
        })
        .parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateSchoolBestuur({
      schoolId: data.schoolId,
      bestuursnummer: data.bestuursnummer,
      bestuursnaam: data.bestuursnaam,
    });
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** PUT — update the school contactgegevens. Returns fieldErrors/formError for the dialog form. */
export const updateSchoolContactgegevensFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator((data: { schoolId: string; input: unknown }) => ({
    schoolId: z.string().min(1).parse(data.schoolId),
    input: contactgegevensAdminSchema.parse(data.input),
  }))
  .handler(async ({ data }) => {
    const value = data.input;
    const result = await updateSchoolContactgegevens({
      schoolId: data.schoolId,
      address: value.address,
      houseNumber: value.houseNumber,
      postalCode: value.postalCode,
      city: value.city,
      phoneNumber: value.phoneNumber,
    });

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/** POST — update the licentie type. */
export const updateLicentieTypeFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; licentieType: "Proefperiode" | "Abonnement" }) =>
    z
      .object({ schoolId: z.string().min(1), licentieType: z.enum(["Proefperiode", "Abonnement"]) })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateLicentieType({
      schoolId: data.schoolId,
      licentieType: data.licentieType,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — update the licentie end date (accepts an ISO datetime). */
export const updateLicentieEndDateFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; endDate: string }) =>
    z.object({ schoolId: z.string().min(1), endDate: z.string().min(1) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateLicentieEndDate({
      schoolId: data.schoolId,
      endDate: data.endDate,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — update the licensed aantal leerlingen. */
export const updateLicentieAantalLeerlingenFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; aantalLeerlingen: number }) =>
    z
      .object({ schoolId: z.string().min(1), aantalLeerlingen: z.number().int().min(0) })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateLicentieAantalLeerlingen({
      schoolId: data.schoolId,
      aantalLeerlingen: data.aantalLeerlingen,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — toggle the verwerkersovereenkomst flag. */
export const updateVerwerkersovereenkomstFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; verwerkersovereenkomst: boolean }) =>
    z.object({ schoolId: z.string().min(1), verwerkersovereenkomst: z.boolean() }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateLicentieVerwerkersovereenkomst({
      schoolId: data.schoolId,
      verwerkersovereenkomst: data.verwerkersovereenkomst,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — toggle the zuurpruim flag. */
export const updateIsZuurpruimFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; isZuurpruim: boolean }) =>
    z.object({ schoolId: z.string().min(1), isZuurpruim: z.boolean() }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateSchoolIsZuurpruim({
      schoolId: data.schoolId,
      isZuurpruim: data.isZuurpruim,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — toggle the exclude-from-statistics flag. */
export const updateExcludeFromStatisticsFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; excludeFromStatistics: boolean }) =>
    z.object({ schoolId: z.string().min(1), excludeFromStatistics: z.boolean() }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await updateSchoolExcludeFromStatistics({
      schoolId: data.schoolId,
      excludeFromStatistics: data.excludeFromStatistics,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — set a docent as hoofddocent. */
export const setHoofdDocentFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; schoolDocentId: string }) =>
    z.object({ schoolId: z.string().min(1), schoolDocentId: z.string().min(1) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await setHoofdDocentForSchool({
      schoolId: data.schoolId,
      schoolDocentId: data.schoolDocentId,
    });
    if (Result.isError(result)) throw new Error(m["common.something_went_wrong"]());
  });

/** POST — remove a docent from a school. */
export const removeDocentFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { schoolId: string; schooldocentId: string }) =>
    z.object({ schoolId: z.string().min(1), schooldocentId: z.string().min(1) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await removeDocentFromSchool({
      schoolId: data.schoolId,
      schooldocentId: data.schooldocentId,
    });
    if (Result.isError(result)) throw new Error(m["admin.errors.docent_verwijder_mislukt"]());
  });

/** POST — invite a docent to a school. */
export const createInviteFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator((data: { schoolId: string; inviteeEmail: string }) =>
    z.object({ schoolId: z.string().min(1), inviteeEmail: z.string().email() }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await createSchooldocentInvite(data.schoolId, data.inviteeEmail);
    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }
    return { message: `Uitnodiging verstuurd naar ${data.inviteeEmail}` };
  });

/** POST — resend a docent invite. */
export const resendInviteFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { inviteId: string }) => z.object({ inviteId: z.string().min(1) }).parse(data))
  .handler(async ({ data }) => {
    const result = await resendSchooldocentInvite(data.inviteId);
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** POST — delete a docent invite. */
export const deleteInviteFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator((data: { inviteId: string }) => z.object({ inviteId: z.string().min(1) }).parse(data))
  .handler(async ({ data }) => {
    const result = await deleteSchooldocentInvite(data.inviteId);
    if (Result.isError(result)) {
      throw new Error(m["invite.errors.delete_failed"]());
    }
  });
